import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Upload, X, GripVertical } from "lucide-react";
import api, { fmtErr } from "../../lib/api";

const inputCls =
  "bg-white border border-black/15 px-3 py-2.5 text-sm font-light focus:outline-none focus:border-black w-full";

const Avatar = ({ photo, name, size = 56 }) => {
  const initials = (name || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return photo ? (
    <img src={photo} alt={name} className="object-cover rounded-full border border-black/10" loading="lazy" decoding="async" style={{ width: size, height: size }} />
  ) : (
    <div className="rounded-full bg-black/5 border border-black/10 flex items-center justify-center text-black/40 label-editorial" style={{ width: size, height: size }}>
      {initials}
    </div>
  );
};

const TestimonialCard = ({ item, onSaved, onDeleted }) => {
  const [draft, setDraft] = useState(item);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => setDraft(item), [item]);
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set({ photo: res.data.url });
      toast.success("Foto carregada. Não esqueça de guardar.");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!draft.name.trim() || !draft.quote.trim()) return toast.error("Nome e opinião são obrigatórios.");
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        role: draft.role?.trim() || "Cliente",
        quote: draft.quote.trim(),
        photo: draft.photo || null,
        order: parseInt(draft.order, 10) || 0,
        active: draft.active !== false,
      };
      const res = await api.put(`/admin/testimonials/${draft.id}`, payload);
      toast.success("Opinião guardada.");
      onSaved(res.data);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remover esta opinião?")) return;
    try {
      await api.delete(`/admin/testimonials/${draft.id}`);
      toast.success("Opinião removida.");
      onDeleted(draft.id);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`testimonial-card-${draft.id}`} className="bg-white border border-black/10 p-6">
      <div className="flex flex-col sm:flex-row gap-6">
        {/* Photo */}
        <div className="flex flex-col items-center gap-3 shrink-0">
          <Avatar photo={draft.photo} name={draft.name} size={80} />
          <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid={`testimonial-file-${draft.id}`} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-1.5 text-xs text-black/50 hover:text-black">
            <Upload size={13} /> {uploading ? "A carregar…" : "Foto"}
          </button>
          {draft.photo && (
            <button type="button" onClick={() => set({ photo: null })} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700">
              <X size={12} /> Remover foto
            </button>
          )}
        </div>

        {/* Fields */}
        <div className="flex-1 space-y-3">
          <div className="flex flex-wrap gap-2">
            <input value={draft.name} onChange={(e) => set({ name: e.target.value })} placeholder="Nome" className={`${inputCls} flex-1 min-w-[160px]`} />
            <input value={draft.role} onChange={(e) => set({ role: e.target.value })} placeholder="Descrição (ex: Cliente)" className={`${inputCls} w-44`} />
          </div>
          <textarea value={draft.quote} onChange={(e) => set({ quote: e.target.value })} rows={3} placeholder="Opinião do cliente" className={inputCls} />
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm font-light">
              <GripVertical size={14} className="text-black/30" /> Ordem
              <input type="number" value={draft.order} onChange={(e) => set({ order: e.target.value })} className={`${inputCls} w-20`} />
            </label>
            <label className="flex items-center gap-2 text-sm font-light cursor-pointer">
              <input type="checkbox" checked={draft.active !== false} onChange={(e) => set({ active: e.target.checked })} /> Visível no site
            </label>
            <div className="flex-1" />
            <button onClick={remove} data-testid={`testimonial-delete-${draft.id}`} className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700">
              <Trash2 size={14} /> Remover
            </button>
            <button onClick={save} disabled={saving} data-testid={`testimonial-save-${draft.id}`} className="flex items-center gap-2 label-editorial bg-black text-white px-6 py-2.5 hover:bg-black/80 transition-colors disabled:opacity-50">
              <Save size={14} /> {saving ? "A guardar…" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const TestimonialsAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = () =>
    api.get("/admin/testimonials").then((res) => setItems(res.data)).finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const addNew = async () => {
    setAdding(true);
    try {
      const res = await api.post("/admin/testimonials", {
        name: "Novo cliente",
        role: "Cliente",
        quote: "Escreva aqui a opinião…",
        order: items.length,
        active: true,
      });
      setItems((p) => [...p, res.data]);
      toast.success("Opinião criada. Edite e guarde.");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const onSaved = (u) => setItems((p) => p.map((x) => (x.id === u.id ? u : x)).sort((a, b) => a.order - b.order));
  const onDeleted = (id) => setItems((p) => p.filter((x) => x.id !== id));

  if (loading) return <p className="label-editorial text-black/40">A carregar…</p>;

  return (
    <div data-testid="testimonials-panel" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-black/50 font-light max-w-2xl">
          Adicione, edite ou remova as opiniões de clientes que aparecem no site. Pode incluir uma foto
          (é redimensionada automaticamente). A ordem controla a sequência de apresentação.
        </p>
        <button onClick={addNew} disabled={adding} data-testid="add-testimonial-btn" className="flex items-center gap-2 border border-black/20 px-5 py-2.5 hover:border-black transition-colors whitespace-nowrap">
          <Plus size={15} /> Nova opinião
        </button>
      </div>
      <div className="space-y-4">
        {items.map((it) => (
          <TestimonialCard key={it.id} item={it} onSaved={onSaved} onDeleted={onDeleted} />
        ))}
        {items.length === 0 && <p className="text-black/40 font-light">Ainda não há opiniões.</p>}
      </div>
    </div>
  );
};

export default TestimonialsAdmin;
